import React from 'react';
import { ChangeEventValue, Coords, Size } from 'google-map-react';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';

import GoogleMap from 'ha/modules/GoogleMap';
import units from 'ha/theme/units';
import FeatureFlag from 'ha/modules/FeatureFlags';

import { PartnerMarker, RoomMarker, ClusterMarker } from './SearchMarkers';
import SearchMapAutoRefresh from '../containers/SearchMapAutoRefresh';
import MapResetButton from './MapResetButton';
import { RoomMarker as RoomMarkerType, UniMarker } from './SearchMarkers/Types';
import { customDistanceToMouse } from '../utils/customDistanceToMouse';
import { Cluster } from '../types';

const MAP_MARGIN = [10, 10, 10, 10];
const HOVER_DISTANCE = 30;

interface Props {
  markers: RoomMarkerType[];
  universities: UniMarker[];
  mapCenter: Coords;
  mapZoom: number;
  onMapChanged: (event: ChangeEventValue) => void;
  highlightedRoomId: number;
  scrollwheel: boolean;
  isMobileMapOpen: boolean;
  isAutoRefreshButtonVisible?: boolean;
  isAutoSearchEnabled?: boolean;
  toggleMapAutoSearch: (isOn: boolean) => void;
  updateBounds: (bounds: object) => void;
  clusters: Cluster[];
  onPinClick: (coords: number[]) => void;
  resetMap: () => void;
}

interface State {
  ignoreOnChange: boolean;
  mapSize: Size;
}

const styles = (t: Theme) =>
  createStyles({
    container: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      transition: 'margin-bottom 0.3s ease-out',
    },
    mapCanvas: {
      width: '100%',
      height: '100%',
    },
    mapResetButton: {
      position: 'absolute',
      left: units.rem(0.75),
      bottom: units.rem(1.5),
      background: t.palette.white.main,
    },
  });

export class SearchMap extends React.Component<
  Props & WithStyles<typeof styles>,
  State
> {
  static defaultProps = {
    highlightedRoomId: 0,
    isAutoRefreshButtonVisible: true,
  };

  state = {
    ignoreOnChange: true,
    mapSize: { width: 0, height: 0 },
  };

  mapContainerRef: React.RefObject<HTMLDivElement> = React.createRef();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapRef: React.MutableRefObject<any> = React.createRef();

  componentDidMount() {
    if (this.props.isAutoSearchEnabled) {
      this.props.toggleMapAutoSearch(true);
    }
  }

  handleOnChange = (mapChange: ChangeEventValue) => {
    const { width: prevWidth, height: prevHeight } = this.state.mapSize;
    const { width, height } = mapChange.size;

    const isMapsSizeChanged = prevWidth !== width || prevHeight !== height;

    if (isMapsSizeChanged) {
      this.setMapSize(mapChange.size);

      return;
    }

    // We don't want to trigger onChange the first time it gets triggered (on first map render).
    // We want the map boundaries to be relevant for search only when the user interacted with it.
    if (this.state.ignoreOnChange) {
      this.setState({ ignoreOnChange: false });
    } else {
      this.props.onMapChanged(mapChange);
    }
  };

  gestureHandling = () => {
    const { isMobileMapOpen } = this.props;

    if (isMobileMapOpen) {
      return 'greedy';
    }

    return 'cooperative';
  };

  // Don't have good typings out of the box, use google doc
  // https://developers.google.com/maps/documentation/javascript/tutorial#google.maps.Map
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleGoogleApiLoaded = ({ map }: any) => {
    this.mapRef.current = map;

    const div = map.getDiv();

    this.setMapSize({
      width: div.offsetWidth,
      height: div.offsetHeight,
    });

    const bounds = map.getBounds();

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    this.props.updateBounds({
      leftLng: sw.lng(),
      rightLng: ne.lng(),
      topLat: ne.lat(),
      bottomLat: sw.lat(),
    });
  };

  setMapSize = (mapSize: Size) => {
    this.setState({ ...this.state, mapSize });
  };

  handlePinClick = (coords: number[]) => {
    const { onPinClick, mapZoom } = this.props;

    if (this.mapRef.current && this.mapRef.current.flyTo) {
      const [lng, lat] = coords;

      this.mapRef.current.flyTo({ lat, lng }, mapZoom + 1);

      return;
    }

    onPinClick(coords);
  };

  render() {
    const {
      classes,
      markers,
      universities,
      mapCenter,
      mapZoom,
      highlightedRoomId,
      scrollwheel,
      isMobileMapOpen,
      isAutoRefreshButtonVisible,
      clusters,
      resetMap,
    } = this.props;

    return (
      <div className={classes.container}>
        <div
          id="map-canvas"
          className={classes.mapCanvas}
          ref={this.mapContainerRef}
        >
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            distanceToMouse={customDistanceToMouse}
            options={{
              scrollwheel: isMobileMapOpen ? false : scrollwheel,
              zoomControl: !isMobileMapOpen,
              fullscreenControl: false,
              gestureHandling: this.gestureHandling(),
            }}
            onChange={this.handleOnChange}
            margin={MAP_MARGIN}
            hoverDistance={HOVER_DISTANCE}
            onGoogleApiLoaded={this.handleGoogleApiLoaded}
          >
            {universities.map(univer => (
              <PartnerMarker
                key={univer.id}
                lat={univer.latitude}
                lng={univer.longitude}
                marker={univer}
                boundariesElement={this.mapContainerRef.current}
              />
            ))}
            {markers.map(marker => (
              <RoomMarker
                key={marker.id}
                lat={marker.latitude}
                lng={marker.longitude}
                marker={marker}
                highlightedRoomId={highlightedRoomId}
                boundariesElement={this.mapContainerRef.current}
              />
            ))}
            {clusters.map((cluster: Cluster) => {
              const { geometry, properties } = cluster;
              const [lng, lat] = geometry.coordinates;

              return (
                <ClusterMarker
                  key={properties.cluster_id}
                  lat={lat}
                  lng={lng}
                  cluster={cluster}
                  onPinClick={this.handlePinClick}
                />
              );
            })}
          </GoogleMap>
          <FeatureFlag>
            {flags => {
              if (flags.searchPageResetMapButton === 'on') {
                return (
                  <MapResetButton
                    className={classes.mapResetButton}
                    onClick={resetMap}
                  />
                );
              }
              if (isAutoRefreshButtonVisible) {
                return <SearchMapAutoRefresh />;
              }

              return null;
            }}
          </FeatureFlag>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(SearchMap);
